import { CSSProperties, useEffect, useRef } from 'react';
import { engine } from '../core/audio/AudioEngine';
import { Pattern } from '../core/project/types';

const COL_W = 30;
const ROW_LABEL_W = 64;

export interface Row {
  note: number;
  label: string;
}

/** FL-style step grid for drum channels (trigger-based view of the note model). */
export function StepGrid({
  pattern,
  rows,
  color,
  onToggle,
}: {
  pattern: Pattern;
  rows: Row[];
  color: string;
  onToggle: (step: number, note: number) => void;
}) {
  const playheadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const el = playheadRef.current;
      if (el) {
        const pos = engine.positionStep % pattern.length;
        el.style.transform = `translateX(${ROW_LABEL_W + pos * COL_W}px)`;
        el.style.opacity = engine.isPlaying ? '1' : '0';
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [pattern.length]);

  return (
    <div className="pe-scroll">
      <div className="pe-grid" style={{ '--ch-color': color } as CSSProperties}>
        <div className="pe-ruler">
          <div className="pe-rowlabel" />
          {Array.from({ length: pattern.length }, (_, i) => (
            <div key={i} className={`pe-stepnum ${i % 4 === 0 ? 'beat' : ''}`}>
              {i % 4 === 0 ? i / 4 + 1 : ''}
            </div>
          ))}
        </div>
        {rows.map((row) => (
          <StepRow key={row.note} row={row} pattern={pattern} onToggle={onToggle} />
        ))}
        <div className="pe-playhead" ref={playheadRef} />
      </div>
    </div>
  );
}

function StepRow({
  row,
  pattern,
  onToggle,
}: {
  row: Row;
  pattern: Pattern;
  onToggle: (step: number, note: number) => void;
}) {
  const painting = useRef<null | 'add' | 'remove'>(null);

  useEffect(() => {
    const up = () => {
      painting.current = null;
    };
    window.addEventListener('pointerup', up);
    return () => window.removeEventListener('pointerup', up);
  }, []);

  const starts = new Set(pattern.notes.filter((n) => n.note === row.note).map((n) => n.start));
  const cells = Array.from({ length: pattern.length }, (_, i) => i);

  return (
    <div className="pe-row">
      <div className="pe-rowlabel">{row.label}</div>
      {cells.map((step) => {
        const on = starts.has(step);
        return (
          <div
            key={step}
            className={`pe-cell ${on ? 'on' : ''} ${step % 4 === 0 ? 'beat' : ''}`}
            onPointerDown={(e) => {
              e.preventDefault();
              painting.current = on ? 'remove' : 'add';
              onToggle(step, row.note);
            }}
            onPointerEnter={() => {
              if (!painting.current) return;
              const activeNow = starts.has(step);
              if ((painting.current === 'add' && !activeNow) || (painting.current === 'remove' && activeNow)) {
                onToggle(step, row.note);
              }
            }}
          />
        );
      })}
    </div>
  );
}